// Role labels and capability checks. The server decides access; these only drive what the UI shows.
import type { Capability, InviteRole, Me, PilotInviteRole, Role } from "./types";

const LABELS: Record<Role, string> = {
  admin: "Superadmin",
  rm: "RM",
  ae: "AE",
  rm_support: "RM support",
  division_manager: "Division manager",
  executive: "Executive",
  member: "Member",
  operations_manager: "Operations manager",
  credit_analyst: "Credit analyst",
  underwriter: "Underwriter",
  doc_funding: "Doc & funding",
  account_manager: "Account manager",
  marketing: "Marketing",
  compliance_reviewer: "Compliance reviewer",
};

export function roleLabel(role: Role | string): string {
  const known = (LABELS as Record<string, string>)[role];
  if (known) return known;
  // The access log records `<own role>_as_<acting role>` while someone acts in a seat.
  const acting = /^([a-z_]+?)_as_([a-z_]+)$/.exec(role);
  if (acting && LABELS[acting[1] as Role] && LABELS[acting[2] as Role]) {
    return `${LABELS[acting[1] as Role]} acting as ${LABELS[acting[2] as Role].toLowerCase()}`;
  }
  return role.replace(/_/g, " ");
}

/** Pilot staff roles an invite can create (with `invite_pilot_staff`). */
export const PILOT_INVITE_ROLES: { value: PilotInviteRole; label: string }[] = (
  ["operations_manager", "credit_analyst", "underwriter", "doc_funding", "account_manager"] as PilotInviteRole[]
).map((value) => ({ value, label: roleLabel(value) }));

export function isPilotInviteRole(role: string): role is PilotInviteRole {
  return PILOT_INVITE_ROLES.some((r) => r.value === role);
}

/** "Invite AE", "Invite RM", "Invite underwriter", … */
export function inviteTitle(role: InviteRole): string {
  if (role === "rm") return "Invite RM";
  if (role === "ae") return "Invite AE";
  return `Invite ${roleLabel(role).replace(/^\w/, (c) => c.toLowerCase())}`;
}

/** Roles an invite can create. `rm_support` is deliberately absent. */
export const INVITE_ROLES: { value: InviteRole; label: string }[] = [
  { value: "ae", label: roleLabel("ae") },
  { value: "rm", label: roleLabel("rm") },
];

/** Every role, for the Superadmin's admin table (which may still assign rm_support / member). */
export const ALL_ROLES: { value: Role; label: string }[] = (
  [
    "ae",
    "rm",
    "rm_support",
    "division_manager",
    "executive",
    "admin",
    "operations_manager",
    "credit_analyst",
    "underwriter",
    "doc_funding",
    "account_manager",
    "marketing",
    "compliance_reviewer",
    "member",
  ] as Role[]
).map((value) => ({ value, label: roleLabel(value) }));

export function hasCap(me: Me, cap: Capability): boolean {
  return (me.user.capabilities ?? []).includes(cap);
}

/** Uses the old "supported AEs" scope (`mine` = the AEs this person supports). */
export function isRmSupport(me: Me): boolean {
  return me.user.role === "rm_support";
}

export function hasPipeline(me: Me): boolean {
  return me.user.role !== "member" && hasCap(me, "view_pipeline");
}

/** Pilot roles that see only loans they are assigned to (access-policy.json `loan_scope: assigned`). */
export const ASSIGNED_ONLY_ROLES: Role[] = ["credit_analyst", "underwriter", "doc_funding", "account_manager"];

export function isAssignedOnly(me: Me): boolean {
  return ASSIGNED_ONLY_ROLES.includes(me.user.role);
}

/** The Prospecting workspace (D25): directory, relationships and follow-ups. Never deal data. */
export function hasProspecting(me: Me): boolean {
  return me.user.role !== "member" && hasCap(me, "view_prospecting");
}

/** The person's own role (the session may be acting in another seat). */
export function realRole(me: Me): Role {
  return me.user.real_role ?? me.user.role;
}

/** The seat this session acts in, or null. */
export function actingSeat(me: Me) {
  return me.user.acting ?? null;
}
